import { NameAvatar, Typography } from '@smartgift/frontend-toolkit';
import styled from 'styled-components';

const AIGiftAssistantTemplateForm = styled.div`
  margin: 0 auto;
  max-width: 648px;
  width: 100%;
`;

const TemplateHeader = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: center;
  margin: 8px auto 40px;
  position: relative;
  width: fit-content;
`;

const TemplateHeaderBadge = styled.div`
  align-items: center;
  background-color: var(--color-blue-50);
  border-radius: 10px;
  display: flex;
  height: 20px;
  justify-content: center;
  padding: 0 8px;
  position: absolute;
  right: -20px;
  top: -8px;
`;

const TemplateForm = styled.form`
  margin: 0 auto;
  position: relative;
  text-align: center;
`;

const TextareaWrapper = styled.div`
  position: relative;

  ${props =>
    props.$animate &&
    `
    &::after {
      animation: pulseRing 0.4s ease-out;
      border-radius: 4px;
      bottom: 0px;
      content: '';
      left: 0px;
      pointer-events: none;
      position: absolute;
      right: 0px;
      top: 0px;
    }
  `}

  @keyframes pulseRing {
    0% {
      transform: scale(1);
      opacity: 1;
      border: 1px solid var(--color-accent-primary-default);
    }
    100% {
      transform: scaleX(1.04) scaleY(1.1);
      opacity: 0;
      border: 2px solid var(--color-accent-primary-default);
    }
  }
`;

const TemplateFormPrompts = styled.div`
  background-color: var(--color-bg-weakest2);
  border: 1px solid var(--color-border-strong);
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  border-top: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0 auto;
  padding: 16px;
  width: calc(100% - 16px);
`;

const TemplateFormPromptsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
`;

const TemplateFormPromptsListItem = styled.button`
  -moz-osx-font-smoothing: grayscale;
  -webkit-font-smoothing: antialiased;
  background-color: var(--color-bg-default);
  border: 1px solid var(--color-border-strong);
  border-radius: 4px;
  cursor: pointer;
  height: 24px;
  max-width: 240px;
  outline: none;
  overflow: hidden;

  /* Minimal anti-blur approach */
  padding: 0 12px;
  text-overflow: ellipsis;
  text-rendering: optimizeLegibility;
  transform-origin: top center;
  transition: background-color 0.2s ease-in-out;

  /* Swinging animation on component load */
  white-space: nowrap;

  &:hover {
    background-color: var(--color-bg-weakest);
    transform: translateX(var(--hover-x, 3px)) scale(var(--hover-scale, 1.05))
      rotate(var(--hover-rotate, 5deg));
    transition: all 0.3s ease-out;
  }

  @keyframes swing {
    0%,
    100% {
      transform: rotate(0deg);
    }
    25% {
      transform: rotate(2deg);
    }
    75% {
      transform: rotate(-1deg);
    }
  }

  @keyframes dropAndSwing {
    0% {
      transform: translateY(-50px) rotate(0deg);
      opacity: 0;
    }
    50% {
      transform: translateY(0px) rotate(0deg);
      opacity: 1;
    }
    65% {
      transform: translateY(0px) rotate(2deg);
    }
    80% {
      transform: translateY(0px) rotate(-1deg);
    }
    100% {
      transform: translateY(0px) rotate(0deg);
    }
  }
`;

const AIGiftAssistantTemplateResults = styled.div`
  margin: 0 auto;
  max-width: 648px;
  width: 100%;
`;

const TemplatePrompt = styled.div`
  background-color: var(--color-bg-weaker);
  border-radius: var(--size-border-radius-md);
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 24px;
  position: relative;

  ${NameAvatar.S.NameAvatar} {
    height: 32px;
  }
`;

const TemplatePromptRewrite = styled.button`
  align-items: center;
  background-color: var(--color-bg-weaker2);
  border: 1px solid var(--color-border-strong);
  border-radius: 2px;
  cursor: pointer;
  display: flex;
  font-size: 12px;
  gap: 2px;
  height: 22px;
  outline: none;
  padding: 0 8px 0 4px;
  transition: background-color 0.2s ease-in-out;
  width: fit-content;

  &:hover {
    background-color: var(--color-bg-weaker);
  }
`;

const TemplatePromptRow = styled.div`
  display: flex;
  gap: 12px;
`;

const TemplatePromptButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const TemplateProcess = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 8px 0;
`;

const TemplateProcessItem = styled.div`
  align-items: center;
  display: flex;
  gap: 2px;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const TemplateProcessItemLoader = styled.div`
  align-items: center;
  display: flex;
  height: 24px;
  justify-content: center;
  width: 24px;
`;

const TemplateProcessAIIcon = styled.div`
  .ai-icon-rotate {
    animation: aiGlow 2s ease-in-out infinite;
  }

  @keyframes aiGlow {
    0%,
    100% {
      filter: drop-shadow(0 0 4px rgba(99, 46, 228, 0.3));
      transform: scale(1);
    }
    50% {
      filter: drop-shadow(0 0 8px rgba(99, 46, 228, 0.6));
      transform: scale(1.05);
    }
  }
`;

const TemplateResults = styled.div`
  display: flex;
  flex-direction: column;
`;

const TemplateResultsList = styled.div`
  background-color: var(--color-bg-weaker);
  border-bottom-left-radius: 0px !important;
  border-bottom-right-radius: 0px !important;
  border-radius: var(--size-border-radius-md);
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(auto-fill, minmax(112px, 1fr));
  padding: 12px;
`;

const TemplateResultsSuggestion = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 24px;
`;

const TemplateResultsSuggestionMessage = styled.div`
  background-color: var(--color-bg-weaker);
  border-bottom-left-radius: 0px !important;
  border-bottom-right-radius: 0px !important;
  border-radius: var(--size-border-radius-md);
  margin-top: 12px;
  padding: 12px;
`;

const AIProductPreview = styled.div`
  border: 1px solid var(--color-border-default);
  border-radius: var(--size-border-radius-md);
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: 184px;
  width: 100%;
`;

const AIProductPreviewBody = styled.div`
  background-color: var(--color-bg-default);
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px 8px;

  ${Typography.S.Typography} {
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    display: -webkit-box;
    overflow: hidden;
  }
`;

const TemplateReSuggest = styled.div`
  background-color: var(--color-bg-weaker);
  border-radius: var(--size-border-radius-md);
  border-top-left-radius: 0px !important;
  border-top-right-radius: 0px !important;
  display: flex;
  justify-content: flex-end;
  padding-bottom: 12px;
  padding-right: 12px;
  width: 100%;
`;

const TemplateReSuggestButton = styled(TemplatePromptRewrite)``;

const TemplateResultsContainer = styled.div`
  border: 1px solid var(--color-border-default);
  border-radius: var(--size-border-radius-md);
  display: ${props => (props.isDisplayed ? 'block' : 'none')};
  margin-bottom: 12px;
  margin-top: 32px;
  padding: 12px;
`;

export {
  AIGiftAssistantTemplateForm,
  AIGiftAssistantTemplateResults,
  AIProductPreview,
  AIProductPreviewBody,
  TemplateForm,
  TemplateFormPrompts,
  TemplateFormPromptsList,
  TemplateFormPromptsListItem,
  TemplateHeader,
  TemplateHeaderBadge,
  TemplateProcess,
  TemplateProcessAIIcon,
  TemplateProcessItem,
  TemplateProcessItemLoader,
  TemplatePrompt,
  TemplatePromptButtonRow,
  TemplatePromptRewrite,
  TemplatePromptRow,
  TemplateReSuggest,
  TemplateReSuggestButton,
  TemplateResults,
  TemplateResultsContainer,
  TemplateResultsList,
  TemplateResultsSuggestion,
  TemplateResultsSuggestionMessage,
  TextareaWrapper
};
